import { alertClasses, AlertProps } from '@mui/material/Alert';
import { alpha, Theme } from '@mui/material/styles';
import { surface } from '../../palette';

// ----------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
const COLORS = ['info', 'success', 'warning', 'error'] as const;

// MUI severities → Figma surfaces (error maps to the critical surface).
const surfaceFor = (color: (typeof COLORS)[number]) =>
  color === 'error' ? surface.critical : surface[color];

// ----------------------------------------------------------------------

export function alert(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: AlertProps) => {
    const standardVariant = ownerState.variant === 'standard';

    const filledVariant = ownerState.variant === 'filled';

    const outlinedVariant = ownerState.variant === 'outlined';

    const colorStyle = COLORS.map(color => ({
      ...(ownerState.severity === color && {
        // STANDARD — light mode uses the Figma semantic surfaces.
        ...(standardVariant && {
          color: lightMode
            ? surfaceFor(color).text
            : theme.palette[color].lighter,
          backgroundColor: lightMode
            ? surfaceFor(color).bg
            : theme.palette[color].darker,
          [`& .${alertClasses.icon}`]: {
            color: lightMode
              ? surfaceFor(color).text
              : theme.palette[color].light
          }
        }),
        // FILLED
        ...(filledVariant && {
          color: theme.palette[color].contrastText,
          backgroundColor: theme.palette[color].main
        }),
        // OUTLINED
        ...(outlinedVariant && {
          backgroundColor: lightMode
            ? surfaceFor(color).bg
            : alpha(theme.palette[color].main, 0.08),
          color: lightMode
            ? surfaceFor(color).text
            : theme.palette[color].light,
          border: `solid 1px ${alpha(theme.palette[color].main, 0.16)}`,
          [`& .${alertClasses.icon}`]: {
            color: lightMode
              ? surfaceFor(color).text
              : theme.palette[color].main
          }
        })
      })
    }));

    return [...colorStyle];
  };

  return {
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AlertProps }) =>
          rootStyles(ownerState),
        icon: {
          opacity: 1
        }
      }
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          marginBottom: theme.spacing(0.5),
          fontWeight: theme.typography.fontWeightSemiBold
        }
      }
    }
  };
}
